import { useMemo } from 'react'

import type { TokenCatalogEntry } from '@/data/marketApi/types'
import { useTokenCatalog } from '@/data/marketApi/hooks/useTokenCatalog'
import { getActiveChainId } from '@/config/active'
import { useNutrimaticMarketOptional } from '@/features/market/NutrimaticMarketProvider'

import { mergeTokenCatalogEntries } from './mergeTokenCatalog'

export type OperativeTokenCatalogSource = 'merged' | 'product-config'

export type OperativeTokenCatalog = {
  entries: TokenCatalogEntry[]
  /** `merged` si hubo instrumentos Nutrimatic; si no, fallback product-config. */
  source: OperativeTokenCatalogSource
  /** Hub presente y catálogo de instrumentos ya no en loading inicial vacío forzado. */
  nutrimaticReady: boolean
}

/**
 * Catálogo operativo para known-check, pickers e import search.
 * Nutrimatic aporta presencia/identidad; product-config aporta seed + policy flags.
 *
 * No usar para `syncTokenPolicyFromCatalog` / marketPolicy — eso sigue en `useTokenCatalog`.
 */
export function useOperativeTokenCatalog(): OperativeTokenCatalog {
  const chainId = getActiveChainId()
  const { entries: productEntries } = useTokenCatalog()
  const hub = useNutrimaticMarketOptional()
  const instruments = hub?.catalog.instruments

  return useMemo(() => {
    const list = instruments ?? []
    const nutrimaticReady = hub != null && !hub.catalog.loading
    if (list.length === 0) {
      return {
        entries: productEntries,
        source: 'product-config' as const,
        nutrimaticReady,
      }
    }
    return {
      entries: mergeTokenCatalogEntries(productEntries, list, chainId),
      source: 'merged' as const,
      nutrimaticReady,
    }
  }, [chainId, hub, instruments, productEntries])
}
